/**
 * Ask running CAD instances to hot-reload their command set after a patch update.
 *
 * The desktop's `patch` verb has already written the new command-set DLL into the live install
 * (the CAD host stayed open); this tells every running instance of the affected product versions
 * to swap to it in place via the plugin's `__reload_commands__` — no restart. It is deliberately
 * **best-effort**: if the agent is down, the token is missing, or an instance predates the
 * method, the files are still on disk and load on the CAD host's next launch, so a failure here
 * degrades to "restart the host to finish updating", never to a broken install.
 */

import { agentClient, type AgentClient } from "./agentClient";
import { bearer, currentAccessToken } from "./accessToken";
import type { PluginVendor } from "../bimgo";

export interface PluginReloadResult {
  requested: number;
  reloaded: number;
}

/**
 * POST `/plugins/reload` for one vendor's product versions. Returns the agent's summary, or `null` if
 * the call could not be made or was rejected — the caller treats `null` as "not reloaded live".
 */
export async function reloadRunningPlugins(
  vendor: PluginVendor,
  hostVersions: string[],
  client: AgentClient = agentClient,
): Promise<PluginReloadResult | null> {
  const token = currentAccessToken();
  if (!token) return null;
  try {
    const response = await client.fetch("/plugins/reload", {
      method: "POST",
      headers: {
        Authorization: bearer(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        vendor,
        hostVersions,
        // Older agents only understand this field and can safely handle Revit requests.
        ...(vendor === "revit" ? { revitVersions: hostVersions } : {}),
      }),
    });
    if (!response.ok) return null;
    const payload = (await response.json().catch(() => null)) as PluginReloadResult | null;
    return payload && typeof payload.reloaded === "number" ? payload : null;
  } catch {
    return null;
  }
}
